import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './AdminFormStyles.css';

interface FormData {
  pixel_id: string;
  access_token: string;
  test_event_code: string;
  name: string;
  is_active: boolean;
  send_purchase_events: boolean;
  send_add_to_cart_events: boolean;
  send_initiate_checkout_events: boolean;
  send_view_content_events: boolean;
}

interface TikTokEventsAPIFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: any;
  loading?: boolean;
}

const defaultForm: FormData = {
  pixel_id: '',
  access_token: '',
  test_event_code: '',
  name: '',
  is_active: true,
  send_purchase_events: true,
  send_add_to_cart_events: true,
  send_initiate_checkout_events: true,
  send_view_content_events: false,
};

const TikTokEventsAPIFormModal: React.FC<TikTokEventsAPIFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
}) => {
  const [formData, setFormData] = useState<FormData>(defaultForm);
  const [errors, setErrors] = useState<any>({});
  const isEditing = Boolean(initialData);

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        pixel_id: initialData.pixel_id || '',
        access_token: '',  // Never pre-fill — write-only field
        test_event_code: initialData.test_event_code || '',
        name: initialData.name || '',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
        send_purchase_events: initialData.send_purchase_events !== undefined ? initialData.send_purchase_events : true,
        send_add_to_cart_events: initialData.send_add_to_cart_events !== undefined ? initialData.send_add_to_cart_events : true,
        send_initiate_checkout_events: initialData.send_initiate_checkout_events !== undefined ? initialData.send_initiate_checkout_events : true,
        send_view_content_events: initialData.send_view_content_events !== undefined ? initialData.send_view_content_events : false,
      });
    } else {
      setFormData(defaultForm);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const checked = type === 'checkbox' ? target.checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: any = {};
    const pixelId = formData.pixel_id.trim().toUpperCase();

    if (!pixelId) {
      newErrors.pixel_id = 'Pixel ID is required';
    } else if (!/^[A-Z0-9]+$/.test(pixelId)) {
      newErrors.pixel_id = 'Pixel ID must contain only letters and numbers';
    } else if (pixelId.length < 10 || pixelId.length > 20) {
      newErrors.pixel_id = 'Pixel ID must be 10–20 characters';
    }

    // Access token required only on create
    if (!isEditing && !formData.access_token.trim()) {
      newErrors.access_token = 'Access Token is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: any = {
      ...formData,
      pixel_id: formData.pixel_id.trim().toUpperCase(),
    };

    // On update: only include access_token if the admin actually typed a new one
    if (isEditing && !formData.access_token.trim()) {
      delete payload.access_token;
    }

    onSubmit(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit TikTok Events API Config' : 'Add TikTok Events API Config'}
      size="large"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">

          {/* Config Name */}
          <div className="admin-form-row">
            <div className="admin-form-label">Config Name</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Main Events API (optional)"
                disabled={loading}
              />
              <span className="admin-form-hint">Optional label for your reference</span>
            </div>
          </div>

          {/* Pixel ID */}
          <div className="admin-form-row">
            <div className="admin-form-label">Pixel ID *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="pixel_id"
                value={formData.pixel_id}
                onChange={handleChange}
                placeholder="e.g., C3AB1234567890"
                className={errors.pixel_id ? 'has-error' : ''}
                disabled={loading}
                autoComplete="off"
              />
              {errors.pixel_id && <span className="error-text">{errors.pixel_id}</span>}
              <span className="admin-form-hint">
                Must match your browser Pixel ID exactly (used for server/browser deduplication)
              </span>
            </div>
          </div>

          {/* Access Token */}
          <div className="admin-form-row">
            <div className="admin-form-label">
              Access Token {!isEditing && '*'}
            </div>
            <div className="admin-form-input">
              <input
                type="password"
                name="access_token"
                value={formData.access_token}
                onChange={handleChange}
                placeholder={isEditing ? 'Leave blank to keep existing token' : 'Paste your Events API Access Token'}
                className={errors.access_token ? 'has-error' : ''}
                disabled={loading}
                autoComplete="new-password"
              />
              {errors.access_token && <span className="error-text">{errors.access_token}</span>}
              {isEditing && (
                <span className="admin-form-hint" style={{ color: '#e6a817' }}>
                  ⚠️ Current token is masked. Enter a new token only if you want to replace it.
                </span>
              )}
              {!isEditing && (
                <span className="admin-form-hint">
                  Found in TikTok Ads Manager → Assets → Events → Manage → Set Up Web Events → Events API
                </span>
              )}
            </div>
          </div>

          {/* Test Event Code */}
          <div className="admin-form-row">
            <div className="admin-form-label">Test Event Code</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="test_event_code"
                value={formData.test_event_code}
                onChange={handleChange}
                placeholder="e.g., TEST12345 (leave blank for production)"
                disabled={loading}
              />
              <span className="admin-form-hint">
                From TikTok Events Manager → Test Events tab. Clear when going live.
              </span>
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Active Configuration</span>
              </label>
            </div>
          </div>

          {/* ── Event Toggles ─────────────────────────────────── */}
          <div className="admin-form-row" style={{ gridColumn: '1/-1', paddingTop: '12px' }}>
            <div className="admin-form-label" style={{ fontWeight: 700 }}>Server-Side Events</div>
            <div className="admin-form-input" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_purchase_events"
                  checked={formData.send_purchase_events}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>PlaceAnOrder (Purchase) — <small>Send when a customer completes checkout</small></span>
              </label>

              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_add_to_cart_events"
                  checked={formData.send_add_to_cart_events}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>AddToCart — <small>Send when a product is added to cart</small></span>
              </label>

              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_initiate_checkout_events"
                  checked={formData.send_initiate_checkout_events}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>InitiateCheckout — <small>Send when checkout page is loaded</small></span>
              </label>

              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_view_content_events"
                  checked={formData.send_view_content_events}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>ViewContent — <small>Send on product page views (⚠️ high volume)</small></span>
              </label>

            </div>
          </div>

          {/* Mode indicator */}
          <div className="admin-form-row" style={{ gridColumn: '1/-1' }}>
            <div className="admin-form-label"></div>
            <div className="admin-form-input">
              {formData.test_event_code ? (
                <div style={{ padding: '10px 14px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '13px' }}>
                  🧪 <strong>Test Mode:</strong> Events will appear in TikTok Events Manager → Test Events with code <code>{formData.test_event_code}</code>. Remove this code when going live.
                </div>
              ) : (
                <div style={{ padding: '10px 14px', backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '6px', fontSize: '13px' }}>
                  ✅ <strong>Production Mode:</strong> Events will be sent to your live TikTok Pixel data.
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="admin-form-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : isEditing ? 'Update Config' : 'Create Config'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TikTokEventsAPIFormModal;
